import React, { Component } from 'react'
import propTypes from 'prop-types'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { Formik, Form, Field } from 'formik'
import { extendObservable } from 'mobx'
import Button from '@components/Button'
import FormError from '@components/FormError/FormError'
import pick from 'lodash/pick'
import Input from '@components/Form/Input'
import RichTextEditor from '@components/Form/RichTextEditor'
import { Dropdown } from '@components/Form/Autocomplete'
import InfoBox from '@components/InfoBox/InfoBox'
import yup from 'yup'
import Spinner from '@components/spinner'
import { configurationApi, complianceApi } from '@atheneum/api'
import Tooltip from '@components/Tooltip'

class ComplianceForm extends Component {
  static propTypes = {
    compliance: propTypes.object,
    modalStore: propTypes.object,
    complianceStore: propTypes.object,
    growlStore: propTypes.object,
    staticData: propTypes.object
  }

  state = { error: null }

  constructor (props) {
    super(props)

    extendObservable(this, {
      employeeReminderText: null,
      loading: true
    })

    this.getEmployeeReminderText()
  }

  saveCompliance = complianceData => {
    const { compliance } = this.props

    configurationApi.update(this.employeeReminderText.id, {
      value: complianceData.employeeReminderText
    })

    if (compliance) {
      return complianceApi.update(compliance.id, complianceData)
    }

    return compliance
      ? complianceApi.update(compliance.id, complianceData)
      :complianceApi.create(complianceData)
  }

  getEmployeeReminderText = async () => {
    try {
      this.employeeReminderText = await configurationApi.getByKey(
        'EMPLOYEE_REMINDER_TEXT'
      )
      this.loading = false
    } catch (err) {
      this.props.growlStore.showError(err.message)
    }
  }

  getInitialValues = function () {
    let { compliance } = this.props
    if (compliance) {
      compliance.employeeReminderText = this.employeeReminderText.value
    }

    let initialValues = {
      acceptLabel: '',
      cancelLabel: '',
      cboxText: '',
      complianceText: '',
      emailBody: '',
      emailSubject: '',
      firstName: '',
      footerText: '',
      lastName: '',
      pageTitle: '',
      signedText: '',
      termsAndConditions: '',
      reminderText: '',
      expertReminderText: '',
      employeeReminderText: this.employeeReminderText.value,
      languageId: null
    }

    initialValues = compliance
      ? pick(compliance, Object.keys(initialValues))
      : initialValues

    return initialValues
  }

  handleSubmit = (values, actions) => {
    const { complianceStore, growlStore, modalStore } = this.props

    this.saveCompliance(values)
      .then(() => {
        complianceStore.getComplianceLanguages()
        growlStore.showSuccess(`Compliance language saved`)
        modalStore.close()
      })
      .catch(({ message }) => {
        this.setState({ error: message })
        actions.setSubmitting(false)
      })
  }

  getPlaceHolders = () => {
    return `
      The following text replacements are available:
      <ul>
        <li>
            <code>{{user.title}}</code> - Experts title (e.g. Mr)
        </li>
        <li>
            <code>{{user.lastName}}</code> - Experts last name
        </li>
        <li>
            <code>{{employee.fullName}}</code> - Full name of the employee that sent request
        </li>
        <li>
            <code>{{url}}</code> - Sites url (e.g. experts.atheneum-partners.com)
        </li>
        <li>
            <code>{{token}}</code> - Request specific token
        </li>
        <br/>
        <li>
            Here is an example of full url link for the user:
            <br/>
            <code>{{url}}/compliance/{{token}}</code>
        </li>
      </ul>
    `
  }

  getEmployeeReminderPlaceholders = () => {
    return `
      The following text replacements are available:
      <ul>
        <li>
          <code>{{expert.title}}</code> - Experts title (e.g. Mr)
        </li>
        <li>
          <code>{{expert.fullName}}</code> - Experts full name
        </li>
        <li>
          <code>{{expert.lastName}}</code> - Experts last name
        </li>
        <li>
          <code>{{employee.title}}</code> - Title of the employee that sent request
        </li>
        <li>
          <code>{{employee.fullName}}</code> - Full name of the employee that sent request
        </li>
        <li>
          <code>{{employee.lastName}}</code> - Last name of the employee that sent request
        </li>        
        <li>
          <code>{{url}}</code> - Site url (e.g. experts.atheneum-partners.com)
        </li>
        <li>
          <code>{{projectUrl}}</code> - Link to the relevant project, e.g. project-search/project/{id}/experts-pipeline
        </li>
      </ul>
    `
  }

  getExpertReminderPlaceholders = () => {
    return `
      The following text replacements are available:
      <ul>
        <li>
          <code>{{expert.title}}</code> - Experts title (e.g. Mr)
        </li>
        <li>
          <code>{{expert.lastName}}</code> - Experts last name
        </li>
        <li>
          <code>{{expert.lastName}}</code> - Experts last name
        </li>
        <li>
          <code>{{employee.title}}</code> - Title of the employee that sent request
        </li>
        <li>
          <code>{{employee.fullName}}</code> - Full name of the employee that sent request
        </li>
        <li>
          <code>{{employee.lastName}}</code> - Last name of the employee that sent request
        </li>
        <li>
          <code>{{url}}</code> - Site url (e.g. experts.atheneum-partners.com)
        </li>
        <li>
          <code>{{token}}</code> - The quick login token used to access the compliance page
        </li>
      </ul>
    `
  }

  getCompliancelLanguageValidationSchema = () => {
    return yup.object().shape({
      languageId: yup
        .number()
        .integer()
        .required('Compliance language is required')
        .typeError('Compliance language is required'),
      acceptLabel: yup.string().required('Accept label is required'),
      cancelLabel: yup.string().required('Cancel label is required'),
      emailSubject: yup.string().required('Email subject is required'),
      emailBody: yup.string().required('Email body is required'),
      complianceText: yup.string().required('Compliance text is required'),
      employeeReminderText: yup
        .string()
        .required('Employee reminder text is required'),
      expertReminderText: yup
        .string()
        .required('Expert reminder text is required'),
      signedText: yup.string().required('Compliance signed message is required')
    })
  }

  /**
   * Allow only unused languages to appear in the language drop-down, in addition to the current selected language.
   */
  getAvailableComplianceLanguages = () => {
    const { complianceStore, compliance, staticData } = this.props
    let complianceLanguageIds = complianceStore.complianceLanguages.map(
      complianceLanguage => complianceLanguage.languageId
    )

    const languageOptions = staticData.languages.filter(
      language =>
        !complianceLanguageIds.includes(language.id) ||
        (!!compliance && language.id === compliance.languageId)
    )

    return languageOptions
  }

  render () {
    if (this.loading) {
      return <Spinner />
    }

    return (
      <div className='compliance-form-wrapper'>
        <Formik
          initialValues={this.getInitialValues()}
          onSubmit={this.handleSubmit}
          validationSchema={this.getCompliancelLanguageValidationSchema()}
          render={form => (
            <div className='expert-form'>
              <h1 className='expert-form__title'>
                {this.props.compliance
                  ? 'Edit compliance language details'
                  : 'Create new compliance language'}
              </h1>
              <FormError error={this.state.error} />
              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  <Field
                    name='languageId'
                    label='Compliance language'
                    placeholder='Select compliance language'
                    itemToString={item => item && item.name}
                    options={this.getAvailableComplianceLanguages()}
                    component={Dropdown}
                  />
                  <Field
                    name='acceptLabel'
                    label='Accept button label'
                    component={Input}
                  />
                  <Field
                    name='cancelLabel'
                    label='Cancel button label'
                    component={Input}
                  />
                  <Field
                    name='emailSubject'
                    label='Email subject'
                    component={Input}
                  />
                  <br />
                  <InfoBox className='placeholders' icon={null}>
                    <h3>Placeholders</h3>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: this.getPlaceHolders()
                      }}
                    />
                  </InfoBox>
                  <br />
                  <br />
                  <Field
                    name='emailBody'
                    label='Email body'
                    includeFont
                    component={RichTextEditor}
                  />
                  <br />
                  <InfoBox className='placeholders' icon={null}>
                    <h3>Expert reminder placeholders</h3>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: this.getExpertReminderPlaceholders()
                      }}
                    />
                  </InfoBox>
                  <br />
                  <br />
                  <Field
                    name='expertReminderText'
                    label={'Expert reminder email body'}
                    includeFont
                    component={RichTextEditor}
                  />
                  <br />
                  <InfoBox className='placeholders' icon={null}>
                    <h3>Employee reminder placeholders</h3>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: this.getEmployeeReminderPlaceholders()
                      }}
                    />
                  </InfoBox>
                  <br />
                  <br />
                  <Field
                    name='employeeReminderText'
                    label={
                      <div>
                        Employee reminder email body
                        <Tooltip
                          placement='top'
                          tooltip='This field is universal to all compliance languages and modifying it will modify it for all languages.'
                        />
                      </div>
                    }
                    includeFont
                    component={RichTextEditor}
                  />
                  <Field
                    name='pageTitle'
                    label='Compliance page title'
                    component={Input}
                  />
                  <Field
                    name='cboxText'
                    label='Checkbox text'
                    component={Input}
                  />
                  <Field
                    name='termsAndConditions'
                    label='Terms and conditions'
                    component={Input}
                  />
                  <Field
                    name='firstName'
                    label='First name'
                    component={Input}
                  />
                  <Field name='lastName' label='Last name' component={Input} />
                  <Field
                    name='footerText'
                    label='Footer text'
                    component={Input}
                  />
                  <Field
                    name='complianceText'
                    label='Compliance text'
                    includeFont
                    component={RichTextEditor}
                  />
                  <Field
                    name='signedText'
                    label='Compliance signed message'
                    includeFont
                    component={RichTextEditor}
                  />
                </div>
                <div className='expert-form__section'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={this.props.modalStore.close}
                    disabled={form.isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

ComplianceForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: true
}

export default withRouter(
  inject('modalStore', 'growlStore', 'staticData')(observer(ComplianceForm))
)
